import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { Seat, SeatSelectHandler, ServicesMap, PassengerFiltersContext } from '../../types';
import {
	buildPassengerFiltersFromSegment,
	buildPassengerSegments,
	getMinSeatChangeChargePrice,
	getTabs
} from '../../utils';
import { MOBILE_MIN_WIDTH, scrollTo, TABLET_MIDDLE_WIDTH } from '../../../utils';
import { useTranslation } from 'react-i18next';
import { PassengerState } from '../../../Checkout/store/passengers/reducers';
import SeatMapServiceContent from './Content/SeatMapServiceContent';
import { useTheme } from '../../../theme';
import Passengers from '../Passengers/Passengers';
import SeatsInfo from '../SeatsInfo/SeatsInfo';
import cn from 'classnames';
import { isSeatOccupied } from '../SeatMap/Seat/utils';
import { ModeContext } from '../../../context';
import { Mode } from '../../../types';
import { RunServiceRequest, Segment } from '../../../Checkout/types';
import SeatsRecommendation from '../../../Checkout/components/Checkout/SeatsRecommendation/SeatsRecommendation';
import { fillOrder } from '../../../Checkout/store/order/actions';
import SeatsRecommendPopup from '../SeatsRecommend/SeatsRecommendPopup/SeatsRecommendPopup';
import { SegmentTabs, SegmentTabType } from '@shared/ui/SegmentTabs';

import {
	AviaPassengerType,
	FlightSeatMapFragment,
	FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	OrderAdditionalServiceGdsServiceSeat,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	PassengerFragment,
	SeatMapPermissions,
	SeatMapRestrictions
} from '@websky/graphql';
import { hasSeatChangeChargeService, isFreeSeat, isUnavailableSeat } from '../SeatMap/utils';
import { useToggleable } from '../../../hooks';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import { SelectedExtraSeat } from '../../../BaggageService/types';
import DoubleSeat from '../Passengers/DoubleSeat/DoubleSeat';
import { useExtraSeat } from '../../hooks';
import { useRenderers } from '../../../renderProps';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import ModalConfirm from '../../../ModalConfirm';
import { buildGdsServicesById } from '@shared/lib/gdsService';

export interface Props {
	className?: string;
	clearOnSegment: (segmentIndex: number) => void;
	seatMaps: FlightSeatMapFragment;
	additionalServices: OrderAdditionalServicesFragment;
	passengers: PassengerState[];
	travellers?: PassengerFragment[];
	onSelect?: SeatSelectHandler;
	onCancelSeat?: SeatSelectHandler;
	selectedExtraSeats?: SelectedExtraSeat[];
	segmentIndex?: number;
	passengerIndex?: number;
	onProceed: () => void;
	setSegment?: (value: number) => void;
	setPassenger?: (value: number) => void;
	onCloseSeatsDialog?: () => void;
	fullScreen?: boolean;
	seatRecommendEnabled?: boolean;
	segments?: Segment[];
	onConfirmRecommendation?: () => void;
	orderId?: string;
	isNewOrder?: boolean;
	fillOrder?: typeof fillOrder;
	runServiceRequest?: RunServiceRequest;
	setSeatCallback?: (seat: Seat[], passengerId: string, segmentId: string) => void;
	confirmRecommendedSeatsWithoutDialog?: boolean;
	onClose?: () => void;
	renderSeatsInfo?: (seatsInfo: OrderAdditionalServiceGdsServiceSeat[], segmentIndex: number) => React.ReactElement;
	onPassengerClear: (passengerId: string) => void;
	dialogRef?: React.RefObject<HTMLDivElement>;
	segmentId?: number;
	activeFilters?: SeatMapRestrictions[];
}

const Component: React.FunctionComponent<Props> = props => {
	const { segmentIndex, setSegment, passengerIndex, setPassenger } = props;

	const [forceGoToSliderStart, setForceGoToSliderStart] = React.useState(0);
	const { isOpen: hasFreeSeats, open: setHasFreeSeats } = useToggleable(false);
	const { isOpen: hasUnavailableSeats, open: setHasUnavailableSeats } = useToggleable(false);
	const { isOpen: hasSeatsWithChangeCharge, open: setHasSeatsWithChangeCharge } = useToggleable(false);
	const { isOpen: hasSeatsWithAnimals, setOpen: setHasSeatsWithAnimals } = useToggleable(false);
	const { isOpen: isRestrictedByFilter, setOpen: setIsRestrictedByFilter } = useToggleable(false);
	const seatMapRef = React.createRef<HTMLDivElement>();
	const mode = useContext(ModeContext);

	const { t } = useTranslation('SeatMap');

	const filteredPassengers = props.passengers.filter(passenger => !(passenger.type === AviaPassengerType.INF)),
		passengerId = filteredPassengers[passengerIndex].id;

	const isReturnFlight = props.segments?.[segmentIndex]?.groupId === '1';

	const amenities = props.segments?.[segmentIndex]?.amenities;

	const segmentParams = React.useMemo(
		() => ({
			seatMaps: props.seatMaps,
			additionalServices: props.additionalServices,
			passengers: props.passengers,
			segmentIndex,
			amenities,
			isNewOrder: props.isNewOrder
		}),
		[props.seatMaps, props.additionalServices, props.passengers, segmentIndex, amenities, props.isNewOrder]
	);

	const segments = React.useMemo(() => {
		const currentPassenger = props.passengers.find(passenger => passenger.id === passengerId);

		return buildPassengerSegments({
			...segmentParams,
			passenger: currentPassenger
		});
	}, [segmentParams, props.passengers, passengerId]);

	const passengerFiltersContext = React.useMemo<PassengerFiltersContext>(() => {
		const context: PassengerFiltersContext = new Map();

		filteredPassengers.forEach(passenger => {
			const passengerSegments = buildPassengerSegments({
				...segmentParams,
				passenger
			});

			context.set(passenger.id, buildPassengerFiltersFromSegment(passengerSegments[segmentIndex]));
		});

		return context;
	}, [segmentParams, filteredPassengers, segmentIndex]);

	const servicesCount = segments[segmentIndex].services?.length;
	const segmentId = segments[segmentIndex].segmentInfo.id;

	const [servicesMap, allServicesMap] = React.useMemo(() => {
		const allServicesMap: ServicesMap = {};
		const canAddedServices: ServicesMap = {};

		const seatServices =
			(props.additionalServices.gdsServices?.services?.filter(
				service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat
			) as FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[]) ?? [];

		seatServices.forEach(service => {
			service.allowedSegments.forEach(allowedSegments => {
				allowedSegments.forEach(allowedSegment => {
					if (!allServicesMap.hasOwnProperty(allowedSegment)) {
						allServicesMap[allowedSegment] = {};
						canAddedServices[allowedSegment] = {};
					}

					service.allowedPassengers.forEach(allowedPassengers => {
						allowedPassengers.forEach(allowedPassenger => {
							if (!allServicesMap[allowedSegment].hasOwnProperty(allowedPassenger)) {
								allServicesMap[allowedSegment][allowedPassenger] = {};
								canAddedServices[allowedSegment][allowedPassenger] = {};
							}

							if (service.canBeAdded) {
								canAddedServices[allowedSegment][allowedPassenger][service.id] = service;
							}

							allServicesMap[allowedSegment][allowedPassenger][service.id] = service;
						});
					});
				});
			});
		});

		return [canAddedServices, allServicesMap];
	}, [props.additionalServices]);

	const gdsServiceById = useMemo(() => {
		return buildGdsServicesById(props.additionalServices.gdsServices?.services ?? []);
	}, [props.additionalServices]);

	const minSeatChangeChargePrice = useMemo(
		() =>
			getMinSeatChangeChargePrice(
				Object.values(gdsServiceById),
				segments[segmentIndex]?.segmentInfo.id,
				passengerId
			),
		[gdsServiceById, segments, segmentIndex, passengerId]
	);

	const theme = useTheme('SeatMap').ComponentStyles;

	const {
		SeatMapPassengers: SeatMapPassengersOverride = Passengers,
		SeatMapServiceContent: SeatMapServiceContentOverride = SeatMapServiceContent
	} = useRenderers();

	React.useEffect(() => {
		scrollTo(0);
	}, []);

	React.useEffect(() => {
		const isPassengerSeatRestricted = props.passengers[props.passengerIndex].seats?.some(
			seat =>
				seat.segmentId === segmentId &&
				seat.restrictions?.some(restriction => props.activeFilters.includes(restriction))
		);

		if (isPassengerSeatRestricted) {
			setIsRestrictedByFilter(true);
		} else {
			setIsRestrictedByFilter(false);
		}
	}, [props.activeFilters, props.passengers[props.passengerIndex].seats, segmentId]);

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

	const onCancel = React.useCallback(
		(passengerId?: string) => {
			const actualPassengerId = passengerId ?? filteredPassengers[passengerIndex]?.id;

			props.onSelect(actualPassengerId, [], segmentIndex);
		},
		[props.onSelect, segmentIndex, passengerIndex]
	);

	const segmentTabs: SegmentTabType[] = React.useMemo(
		() =>
			getTabs(segments, props.passengers, servicesMap).map(item => {
				const { value, header, isAvailable, seatsCount } = item;

				return {
					id: value.toString(),
					title: header,
					isDisabled: !isAvailable,
					countSeatsSelected: seatsCount
				};
			}),
		[props.passengers]
	);

	const selectNextPassengerWithoutSeat = () => {
		const currentSegmentId = segments[segmentIndex].segmentInfo.id;

		const nextPassengerWithoutSeat = filteredPassengers
			.filter(passenger => passenger.id !== passengerId)
			.find(passenger => !passenger.seats || !passenger.seats.some(seat => seat.segmentId === currentSegmentId));

		if (nextPassengerWithoutSeat) {
			const nextPassengerWithoutSeatIndex = filteredPassengers.findIndex(
				passenger => nextPassengerWithoutSeat.id === passenger.id
			);

			setPassenger(nextPassengerWithoutSeatIndex);
		}
	};

	const onSelectNextFlight = React.useCallback(() => {
		if (segmentIndex + 1 < props.seatMaps.segments.length) {
			setSegment(segmentIndex + 1);
			setPassenger(0);
		}
	}, [segmentIndex, props.seatMaps.segments]);

	const onSelectPrevFlight = React.useCallback(() => {
		if (segmentIndex > 0) {
			setSegment(segmentIndex - 1);
			setPassenger(0);
		}
	}, [segmentIndex]);

	const onClear = React.useCallback(() => {
		props.clearOnSegment(segmentIndex);
	}, [segmentIndex, props.clearOnSegment, props.passengers]);

	const onSelect = React.useCallback(
		async (seats: Seat[], passengerId: string) => {
			const currentPassenger = filteredPassengers.find(passenger => passenger.id === passengerId);

			const isOccupied = seats.some(seat =>
				isSeatOccupied(seat, currentPassenger, segments[segmentIndex].segmentInfo.id)
			);

			if (!isOccupied) {
				props.onSelect(passengerId, seats, segmentIndex);
				selectNextPassengerWithoutSeat();
			} else {
				// onCancel();
				props.onCancelSeat(passengerId, seats, segmentIndex);
			}
		},
		[props.onSelect, segmentIndex, filteredPassengers]
	);

	const hideControls = React.useMemo(
		() => segments.every(segment => segment.isSeatsAvailableInCheckinOnly) && mode !== Mode.Checkin,
		[segments]
	);

	/**
	 * Объединил получение инфы о наличии мест с животными, бесплатными местами и недоступными местами
	 * если на сегменте находятся все такие места, цикл прекращается
	 * в противном случае, проверят до конца
	 */
	React.useEffect(() => {
		for (const deck of segments[segmentIndex].decks) {
			for (const row of deck.rows) {
				for (const seat of row.seats) {
					if (hasSeatsWithAnimals && hasFreeSeats && hasUnavailableSeats && hasSeatsWithChangeCharge) {
						break;
					}

					if (!hasSeatsWithAnimals && seat.permissions?.includes(SeatMapPermissions.WithAnimals)) {
						setHasSeatsWithAnimals(true);
					}

					if (!hasFreeSeats && isFreeSeat(seat)) {
						setHasFreeSeats();
					}

					if (!hasUnavailableSeats && isUnavailableSeat(seat)) {
						setHasUnavailableSeats();
					}

					if (!hasSeatsWithChangeCharge && hasSeatChangeChargeService(seat, gdsServiceById, passengerId)) {
						setHasSeatsWithChangeCharge();
					}
				}
			}
		}
	}, [
		segments,
		segmentIndex,
		hasFreeSeats,
		hasUnavailableSeats,
		hasSeatsWithChangeCharge,
		passengerId,
		gdsServiceById
	]);

	React.useEffect(() => {
		if (isTablet) {
			setPassenger(0);
		}
	}, [segmentIndex]);

	const hasSelectedSeats = useMemo(() => {
		return !!props.passengers.find(passenger => passenger.id === passengerId)?.seats?.length;
	}, [props.passengers, passengerId]);

	const passengersIdsWithExtraSeats = useMemo(() => {
		const filteredPassengers = isTablet
			? props.passengers
			: props.passengers.filter(passenger => passenger.id === passengerId);

		return filteredPassengers
			.filter(passenger =>
				props.selectedExtraSeats?.find(
					extraSeat =>
						extraSeat.passengerId === passenger.id && extraSeat.segmentIds.includes(segmentIndex.toString())
				)
			)
			.map(passenger => passenger.id);
	}, [props.passengers, props.selectedExtraSeats, passengerId, segmentIndex, isTablet]);

	const { showDoubleSeats, extraSeatService, toggleExtraSeatService } = useExtraSeat(
		segments,
		passengerId,
		segmentIndex
	);

	const toggleExtraSeatServiceHandler = useCallback(
		(active: boolean) => {
			if (hasSelectedSeats) {
				props.onPassengerClear(passengerId);
			}

			toggleExtraSeatService(active, passengerId);
		},
		[passengerId, hasSelectedSeats, props.onPassengerClear]
	);

	const renderDoubleSeats = React.useCallback(() => {
		if (showDoubleSeats) {
			return (
				<DoubleSeat
					description={!isTablet ? extraSeatService.description || '' : null}
					isActive={passengersIdsWithExtraSeats.length > 0}
					hasSeatsByPassenger={hasSelectedSeats}
					showInfo={segments.length > 1}
					toggleExtraSeat={toggleExtraSeatServiceHandler}
				/>
			);
		}

		return null;
	}, [
		showDoubleSeats,
		extraSeatService,
		passengersIdsWithExtraSeats,
		toggleExtraSeatService,
		hasSelectedSeats,
		segments,
		isTablet
	]);

	const onChangePassenger = (passengerId: string) => {
		const currentIndex = filteredPassengers.findIndex(passenger => passenger.id === passengerId);

		if (currentIndex !== -1) {
			setPassenger(currentIndex);
		} else {
			const dIndex = +passengerId - +filteredPassengers[passengerIndex].id;
			const nextPassengers = filteredPassengers.filter(passenger =>
				dIndex > 0 ? passenger.id >= passengerId : passenger.id <= passengerId
			);

			const nextPassengerId = nextPassengers[dIndex > 0 ? 0 : nextPassengers.length - 1]?.id;
			const nextPassengerIndex = nextPassengerId
				? filteredPassengers.findIndex(passenger => passenger.id === nextPassengerId)
				: 0;

			// add it to force trigger slider useEffect when passengerIndex stays the same
			setForceGoToSliderStart(prev => prev + 1);
			setPassenger(nextPassengerIndex);
		}
	};

	const renderPassengers = React.useCallback(() => {
		return (
			<SeatMapPassengersOverride
				hideDisclaimer={hideControls}
				hideNextButton={hideControls}
				hidePassengers={hideControls}
				services={allServicesMap}
				onProceed={props.onProceed}
				seatMapRef={seatMapRef}
				passengers={props.passengers}
				segmentId={segmentId}
				passengerId={passengerId}
				forceGoToSliderStart={forceGoToSliderStart}
				onSelectNextFlight={onSelectNextFlight}
				segmentIndex={segmentIndex}
				segments={segments}
				hasSeatsWithAnimals={hasSeatsWithAnimals}
				onChange={onChangePassenger}
				onCancel={onCancel}
				onClear={onClear}
				renderDoubleSeats={renderDoubleSeats}
				hasSelectedExtraSeats={!!props.selectedExtraSeats.length}
				passengersIdsWithExtraSeats={passengersIdsWithExtraSeats}
				toggleExtraSeat={showDoubleSeats ? toggleExtraSeatServiceHandler : null}
				isNewOrder={props.isNewOrder}
				isDialog={!!props.dialogRef?.current}
				activeFilters={props.activeFilters}
				passengerFiltersContext={passengerFiltersContext}
			/>
		);
	}, [
		props.passengers,
		segmentIndex,
		passengerId,
		forceGoToSliderStart,
		hasSeatsWithAnimals,
		renderDoubleSeats,
		passengersIdsWithExtraSeats,
		toggleExtraSeatServiceHandler,
		props.selectedExtraSeats,
		props.isNewOrder,
		props.dialogRef,
		props.activeFilters,
		passengerFiltersContext
	]);

	const renderSeatsInfo = React.useCallback(() => {
		if (segments[segmentIndex].services) {
			return props.renderSeatsInfo ? (
				props.renderSeatsInfo(segments[segmentIndex].services as any[], segmentIndex)
			) : (
				<SeatsInfo
					hasFreeSeats={hasFreeSeats}
					hasUnavailableSeats={hasUnavailableSeats}
					minSeatChangeChargePrice={hasSeatsWithChangeCharge ? minSeatChangeChargePrice : undefined}
					seatsInfo={segments[segmentIndex].services}
					amenities={amenities}
					segmentId={segmentIndex.toString()}
					services={servicesMap}
					passengerId={passengerId}
				/>
			);
		}

		return null;
	}, [
		segments,
		segmentIndex,
		hasFreeSeats,
		props.renderSeatsInfo,
		passengerId,
		hasUnavailableSeats,
		hasSeatsWithChangeCharge,
		minSeatChangeChargePrice
	]);

	const renderTabs = React.useCallback(
		() => (
			<SegmentTabs
				title={t('Choose your seat')}
				activeSegment={segmentIndex.toString()}
				segments={segmentTabs}
				center={true}
				sticky={true}
				onSegmentClick={segment => setSegment(parseInt(segment))}
				onClose={props.onClose}
			/>
		),
		[segmentTabs, segmentIndex, setSegment]
	);

	const onCloseModal = () => setIsRestrictedByFilter(false);

	const onChangeSeatInModal = React.useCallback(() => {
		onCloseModal();
		onSelect(props.passengers[passengerId].seats, passengerId);
	}, [onSelect, props.passengers[passengerId].seats, passengerId]);

	const seatMapRecommendEnabled = React.useMemo(() => {
		return props.seatRecommendEnabled && !hideControls;
	}, [props.seatRecommendEnabled, hideControls]);

	useEffect(() => {
		WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.SeatsList, { segmentId: segmentIndex.toString() });
	}, [segmentIndex]);

	return (
		<>
			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>{renderTabs()}</MediaQuery>

			<div
				className={cn(theme.wrapper, props.className, {
					[theme.wrapper_threeServices]: servicesCount === 3,
					[theme.wrapper_fourServices]: servicesCount === 4
				})}
			>
				<SeatsRecommendation
					orderId={props.orderId}
					segments={props.segments}
					travellers={props.travellers}
					fillOrder={props.fillOrder}
					runServiceRequest={props.runServiceRequest}
					segmentIndex={segmentIndex}
					passengerId={passengerId}
					isEnabled={seatMapRecommendEnabled}
					setSeat={props.setSeatCallback}
					confirmRecommendedSeatsWithoutDialog={props.confirmRecommendedSeatsWithoutDialog}
					seatMap={props.seatMaps}
				>
					<SeatMapServiceContentOverride
						allSeatServices={allServicesMap}
						services={servicesMap}
						gdsServiceById={gdsServiceById}
						fullScreen={props.fullScreen}
						segments={segments}
						onSelect={onSelect}
						passengers={filteredPassengers}
						seatMapRef={seatMapRef}
						onSelectNextFlight={onSelectNextFlight}
						passengerId={passengerId}
						segmentIndex={segmentIndex}
						swipeBetweenSegments={true}
						activeDoubleSeats={passengersIdsWithExtraSeats.includes(passengerId)}
						renderPassengers={renderPassengers}
						renderSeatsInfo={renderSeatsInfo}
						onProceed={props.onProceed}
						onBack={onSelectPrevFlight}
						onClear={onClear}
						dialogRef={props.dialogRef}
						isReturnFlight={isReturnFlight}
						activeFilters={props.activeFilters}
					/>

					<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
						<SeatsRecommendPopup
							passengerId={passengerId}
							segmentId={segmentIndex}
							services={servicesMap}
							setPassenger={setPassenger}
							passengers={filteredPassengers}
						/>
					</MediaQuery>

					<ModalConfirm
						size="medium"
						isOpen={isRestrictedByFilter}
						onClose={onCloseModal}
						title={t('Oops...')}
						cancelBtn={t('No, keep this seat')}
						confirmBtn={t('Yes, choose another seat')}
						onCancel={onCloseModal}
						onConfirm={onChangeSeatInModal}
					>
						{t('Your current seat doesn\'t match the selected filter. Do you want to change your seat?')}
					</ModalConfirm>
				</SeatsRecommendation>
			</div>
		</>
	);
};

export default Component;
